import { isPastPresentOrFuture, PAST, PRESENT, FUTURE } from './dates'

/**
 * Calculate a user's score stats for a week
 * Returns current confirmed total and number of remaining (unplayed) days
 */
const getUserWeekStats = (weekData, username, groupCreatedAt) => {
  let currentTotal = 0
  let remainingDays = 0

  for (const dateStr in weekData.data) {
    // Skip days before group creation
    if (groupCreatedAt && dateStr < groupCreatedAt) continue

    const dayData = weekData.data[dateStr]
    const status = isPastPresentOrFuture(dateStr)
    const userScore = dayData[username]

    if (status === PAST) {
      // Past day: use actual score or 8 (failed) if missing
      currentTotal += userScore ?? 8
    } else if (status === PRESENT) {
      // Today: use actual score if entered, otherwise it's a remaining day
      if (userScore != null) {
        currentTotal += userScore
      } else {
        remainingDays++
      }
    } else if (status === FUTURE) {
      // Future day: counts as remaining
      remainingDays++
    }
  }

  return { currentTotal, remainingDays }
}

/**
 * Determine if a week's outcome is mathematically decided
 * 
 * Returns:
 * - 'win': User has guaranteed lowest score (strictly lower than all opponents)
 * - 'loss': At least one opponent has guaranteed lower score than user
 * - 'tie': Week complete but tied for lowest
 * - 'undecided': Outcome could still change
 * - null: Not applicable (personal scope, single player, etc.)
 */
export const getWeekOutcome = (weekData, username, allUsernames, groupCreatedAt) => {
  if (!weekData || !username || !allUsernames || allUsernames.length < 2) {
    return null
  }

  const userStats = getUserWeekStats(weekData, username, groupCreatedAt)
  const opponentStats = allUsernames
    .filter(u => u !== username)
    .map(u => ({
      username: u,
      ...getUserWeekStats(weekData, u, groupCreatedAt)
    }))

  // User's worst possible final score (current + 8 for each remaining day)
  const userWorstCase = userStats.currentTotal + (userStats.remainingDays * 8)
  // User's best possible final score (current + 1 for each remaining day)
  const userBestCase = userStats.currentTotal + (userStats.remainingDays * 1)

  // Check for guaranteed WIN:
  // Even if user gets worst possible scores (all 8s) and opponents get best (all 1s),
  // user still has the strictly lowest total
  const hasGuaranteedWin = opponentStats.every(opp => {
    const oppBestCase = opp.currentTotal + (opp.remainingDays * 1)
    return userWorstCase < oppBestCase
  })

  if (hasGuaranteedWin) {
    return 'win'
  }

  // Check for guaranteed LOSS:
  // At least one opponent's worst possible score is still strictly lower than user's best
  const hasGuaranteedLoss = opponentStats.some(opp => {
    const oppWorstCase = opp.currentTotal + (opp.remainingDays * 8)
    return oppWorstCase < userBestCase
  })

  if (hasGuaranteedLoss) {
    return 'loss'
  }

  // Check if week is complete (no remaining days for anyone)
  const isWeekComplete = userStats.remainingDays === 0 &&
    opponentStats.every(opp => opp.remainingDays === 0)

  if (isWeekComplete) {
    const userFinal = userStats.currentTotal
    const oppFinals = opponentStats.map(opp => opp.currentTotal)
    const minOppFinal = Math.min(...oppFinals)

    if (userFinal < minOppFinal) {
      return 'win'
    } else if (userFinal === minOppFinal) {
      // Tied for lowest - no clear winner
      return 'tie'
    } else {
      // Check if there's a clear winner (not a multi-way tie)
      const allScores = [userFinal, ...oppFinals]
      const minScore = Math.min(...allScores)
      const winnersCount = allScores.filter(s => s === minScore).length

      if (winnersCount === 1) {
        // There's a clear winner and it's not the user
        return 'loss'
      } else {
        // Multi-way tie (user not involved) - still a loss for user but with tie context
        return 'loss'
      }
    }
  }

  return 'undecided'
}

/**
 * Get the localStorage key for tracking shown animations
 */
export const getAnimationStorageKey = (userId, groupId, weekStart) => {
  return `weekResultShown:${userId}:${groupId}:${weekStart}`
}

/**
 * Check if animation was already shown for this user/group/week
 */
export const wasAnimationShown = (userId, groupId, weekStart) => {
  const key = getAnimationStorageKey(userId, groupId, weekStart)
  return localStorage.getItem(key) === 'true'
}

/**
 * Mark animation as shown
 */
export const markAnimationShown = (userId, groupId, weekStart) => {
  const key = getAnimationStorageKey(userId, groupId, weekStart)
  localStorage.setItem(key, 'true')
}

/**
 * Format week start date for display (e.g., "Dec 22, 2025")
 */
export const formatWeekForDisplay = (weekStart) => {
  const date = new Date(weekStart + 'T00:00:00')
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}